/**
 * The OAuth request an AI assistant (ChatGPT, Claude) sends a user through
 * when they connect it to GreenGrowth (backend: lib/mcpOAuth.ts).
 *
 * The backend sends a signed-out user to /login and a signed-in one to
 * /oauth/consent, with the request in the query string, signed (`sig`) and
 * listing its own parameter names (`ba_param`). Posting that query back as
 * `oauth_query` with sign-in, email verification or consent lets the backend
 * continue the flow; it answers `{ redirect: true, url }` with the next page.
 */

const PENDING_KEY = "gg.oauthRequest";
/** The backend signs the query for 10 minutes; a stale copy must not hijack a later login. */
const PENDING_TTL_MS = 10 * 60 * 1000;

/**
 * The signed OAuth query in a location.search, or null. Mirrors the backend
 * client's buildSignedOAuthQuery: keep `sig`, `ba_param`, and the parameters
 * `ba_param` names — anything else in the URL is not part of the signature.
 */
export function readOAuthQuery(search: string): string | null {
  const params = new URLSearchParams(search);
  if (!params.has("sig")) return null;
  const signed = new Set(params.getAll("ba_param"));
  if (signed.size === 0) return null;
  const out = new URLSearchParams();
  params.forEach((value, key) => {
    if (key === "sig" || key === "ba_param" || signed.has(key)) out.append(key, value);
  });
  return out.toString();
}

/**
 * Keep the request across the Register → Verify email detour (the router
 * drops the query string). Per tab, and only for as long as it is valid.
 */
export function savePendingOAuthQuery(query: string): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ query, at: Date.now() }));
  } catch {
    /* without storage the detour just ends in the app instead of the assistant */
  }
}

export function peekPendingOAuthQuery(): string | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { query, at } = JSON.parse(raw) as { query?: string; at?: number };
    if (!query || !at || Date.now() - at > PENDING_TTL_MS) {
      window.sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    return query;
  } catch {
    return null;
  }
}

export function clearPendingOAuthQuery(): void {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/** The OAuth request on this page, else the one saved before a detour. */
export function currentOAuthQuery(search: string): string | null {
  return readOAuthQuery(search) ?? peekPendingOAuthQuery();
}

/** Host the assistant's redirect points at ("chatgpt.com") — the part of the request a client cannot fake. */
export function oauthRedirectHost(query: string): string | null {
  const uri = new URLSearchParams(query).get("redirect_uri");
  if (!uri) return null;
  try {
    return new URL(uri).host;
  } catch {
    return null;
  }
}

/**
 * Follow the backend's `{ redirect: true, url }` answer with a full page
 * navigation (it leaves the app for the assistant, or goes to the consent
 * screen). Returns false when the response is not such an answer.
 */
export function followOAuthRedirect(data: unknown): boolean {
  const d = data as { redirect?: boolean; url?: unknown } | null;
  if (!d?.redirect || typeof d.url !== "string") return false;
  window.location.assign(d.url);
  return true;
}
